import { Heading, PlugMark, Text, cn } from '@ui'
import React, { ReactNode } from 'react'

interface SetupStepProps {
  title?: ReactNode
  description?: ReactNode
  /** Replaces the default mark above the title. */
  hero?: ReactNode
  /**
   * Inside the app shell (e.g. backup seed opened from Settings) the step fills its route
   * container instead of owning the viewport.
   */
  contained?: boolean
  className?: string
  children?: ReactNode
}

/**
 * Narrow centred column used by every setup / unlock step. Owns the viewport
 * (`flex: 0 0 auto; height: 100dvh; overflow-y: auto`, plan C9) so a tall step scrolls
 * instead of being clipped; `m-auto` keeps it centred while it fits.
 */
export const SetupStep = ({
  title,
  description,
  hero,
  contained,
  className,
  children,
}: SetupStepProps) => (
  <div
    className={cn(
      'flex w-full flex-col overflow-x-hidden overflow-y-auto px-6 py-10 text-fg',
      contained ? 'min-h-0 flex-1' : 'h-dvh flex-[0_0_auto]'
    )}
  >
    <div className={cn('m-auto flex w-full max-w-[400px] flex-col gap-6', className)}>
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="mb-2">
          {hero ?? <PlugMark variant="auto" size={88} motion="float" enter />}
        </div>
        {title && (
          <Heading level={3} align="center">
            {title}
          </Heading>
        )}
        {description && (
          <Text variant="body-s" tone="fg-2" align="center">
            {description}
          </Text>
        )}
      </div>
      {children}
    </div>
  </div>
)
